/**
 * /register — practitioner / student_alumni invitation acceptance.
 *
 * Flow:
 *   1. read ?token & ?email from URL
 *   2. GET /api/auth/admin/invite/validate → show form or error card
 *   3. POST /api/auth/admin/accept-invite → success state → /login
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const email = params.get("email") || "";

  const [phase, setPhase] = useState("checking"); // checking|invalid|form|submitting|success
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [license, setLicense] = useState("");

  useEffect(() => {
    if (!token || !email) { setPhase("invalid"); setError("Missing invitation link parameters."); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/admin/invite/validate`, { params: { token, email } });
        if (r.data?.valid) {
          setInvite(r.data);
          setName(r.data.name || "");
          setPhase("form");
        } else {
          setPhase("invalid");
          setError(r.data?.reason === "expired"
            ? "This invitation link has expired. Please request a new one from your KCIS administrator."
            : "This invitation link is invalid. Please check the URL or request a new invitation.");
        }
      } catch (e) {
        setPhase("invalid");
        setError("Could not verify invitation. Please try again or contact support.");
      }
    })();
  }, [token, email]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setPhase("submitting");
    try {
      const r = await axios.post(`${API}/auth/admin/accept-invite`, { token, new_password: password });
      setInvite((i) => ({ ...i, ...r.data }));
      setPhase("success");
      toast.success("Account ready");
      setTimeout(() => navigate("/admin/login"), 3200);
    } catch (e) {
      const msg = e.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Registration failed");
      setPhase("form");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <header className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-purple-900">
            <Sparkles className="w-5 h-5" />
            <span className="font-display text-lg tracking-wide">KCIS / KISSED University</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Accept Your Invitation</p>
        </header>

        {phase === "checking" && (
          <Card>
            <div className="py-8 text-center text-slate-600 inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-4 h-4 animate-spin" />Verifying your invitation…
            </div>
          </Card>
        )}

        {phase === "invalid" && (
          <Card data-testid="register-invalid-card">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-rose-600 flex-shrink-0" />
              <div>
                <h2 className="font-display text-lg text-slate-900">Invitation Not Available</h2>
                <p className="text-sm text-slate-600 mt-2">{error}</p>
                <p className="text-xs text-slate-500 mt-4">
                  Need help? Email <a className="text-purple-900 underline" href="mailto:registrar@kissedbycvon.com">registrar@kissedbycvon.com</a>
                </p>
              </div>
            </div>
          </Card>
        )}

        {phase === "form" && invite && (
          <Card>
            {invite.account_type === "student_alumni" && (
              <div className="mb-4 rounded-xl bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-900" data-testid="register-comp-banner">
                <b>Alumni Welcome.</b> Your 30-day KCIS Pro complimentary access will activate the moment you set your password.
              </div>
            )}
            <h2 className="font-display text-xl text-slate-900 mb-1">Set your password</h2>
            <p className="text-xs text-slate-500 mb-4">Welcome to KCIS, {invite.name || invite.email}.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="register-form">
              <Field label="Email"><input className="form-input bg-slate-100" value={invite.email} readOnly data-testid="register-email" /></Field>
              <Field label="Name"><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" /></Field>
              <Field label="Password (min 8)"><input className="form-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required data-testid="register-password" /></Field>
              <Field label="Confirm password"><input className="form-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="register-confirm" /></Field>
              <Field label="Specialty (optional)"><input className="form-input" value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="e.g. Holistic Trichology" data-testid="register-specialty" /></Field>
              <Field label="License number (optional)"><input className="form-input" value={license} onChange={(e) => setLicense(e.target.value)} data-testid="register-license" /></Field>

              <button type="submit" className="w-full rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-11 inline-flex items-center justify-center gap-2" data-testid="register-submit">
                Activate Account
              </button>
              <p className="text-[11px] text-slate-500 text-center">By activating, you agree to the KCIS Practitioner Terms.</p>
            </form>
          </Card>
        )}

        {phase === "submitting" && (
          <Card><div className="py-8 text-center text-slate-600 inline-flex items-center justify-center gap-2 w-full"><Loader2 className="w-4 h-4 animate-spin" />Activating your account…</div></Card>
        )}

        {phase === "success" && (
          <Card data-testid="register-success-card">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div>
                <h2 className="font-display text-lg text-slate-900">Your account is ready</h2>
                <p className="text-sm text-slate-600 mt-2">
                  Redirecting you to the sign-in page…
                  {invite?.account_type === "student_alumni" && (
                    <span className="block mt-2 text-purple-900">Your 30-day complimentary KCIS Pro access is now active.</span>
                  )}
                </p>
                <Link to="/admin/login" className="mt-3 inline-block text-xs text-purple-900 underline">Sign in now →</Link>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ children, ...rest }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6" {...rest}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wide text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
